import { Box, Typography, Paper } from '@mui/material';
import PropTypes from 'prop-types';

function JobStatisticsHeader({ job }) {
	const { status, statistics } = job;
	
	const getStatisticValue = (ingestedKey, syncedKey) => {
		const isIngestedWithCount =
			status === 'ingested' &&
			Object.prototype.hasOwnProperty.call(statistics, ingestedKey);
		const isSyncedWithCount =
			status === 'synced' &&
			Object.prototype.hasOwnProperty.call(statistics, syncedKey);

		if (isIngestedWithCount) {
			return statistics[ingestedKey];
		} else if (isSyncedWithCount) {
			return statistics[syncedKey];
		} else {
			// if job is not ingested/synced, or ingested/synced key not found, display '-'
			return '-';
		}
	};

	//Labels: ACCOUNTS | CONTACTS | INTERACTIONS | INTERACTION REFERENCES | INTERACTION MESSAGES
	const statisticsItems = [
		{
			label: 'ACCOUNTS',
			value: getStatisticValue('accounts_ingested', 'accounts_synced'),
			testId: 'accounts-section',
		},
		{
			label: 'CONTACTS',
			value: getStatisticValue('contacts_ingested', 'contacts_synced'),
			testId: 'contacts-section',
		},
		{
			label: 'INTERACTIONS',
			value: getStatisticValue('interactions_ingested', 'interactions_synced'),
			testId: 'interactions-section',
		},
		{
			label: 'INTERACTION REFERENCES',
			value: getStatisticValue(
				'interaction_references_ingested',
				'interaction_references_synced'
			),
			testId: 'interaction-references-section',
		},
		{
			label: 'INTERACTION MESSAGES',
			value: getStatisticValue(
				'interaction_messages_ingested',
				'interaction_messages_synced'
			),
			testId: 'interaction-messages-section',
		},
	];

	// ingested job will show counts with the text "ready to sync"
	// synced job will show counts with the text "synced"
	const getStatusText = () => {
		if (job.status === 'ingested') {
			return { text: 'ready to sync', color: '#1976d2', fontWeight: 550 };
		} else if (job.status === 'synced') {
			return { text: 'synced', color: '#4caf50', fontWeight: 550 };
		} else {
			return { text: '', color: 'transparent', fontWeight: 550 };
		}
	};

	const statusInfo = getStatusText();

	const Label = ({ label }) => (
		<Typography
			variant='caption'
			sx={{
				color: '#9e9e9e',
				fontWeight: 600,
				fontSize: '11px',
				letterSpacing: '0.5px',
			}}
		>
			{label}
		</Typography>
	);

	const Count = ({ value }) => (
		<Typography
			variant='h6'
			sx={{
				fontSize: '24px',
				fontWeight: 600,
			}}
		>
			{value.toLocaleString()}
		</Typography>
	);

	const StatusInfo = ({ statusInfo }) => (
		<Typography
			variant='caption'
			sx={{
				color: statusInfo.color,
				fontSize: '12px',
				fontWeight: statusInfo.fontWeight,
			}}
		>
			{statusInfo.text}
		</Typography>
	);

	return (
		<Paper
			variant='outlined'
			sx={{
				display: 'grid',
				gridTemplateColumns: 'repeat(5, 1fr)',
				gap: 2,
				px: 3,
				mb: 2,
				borderRadius: 1,
			}}
		>
			{statisticsItems.map((item, index) => (
				<Box
					key={index}
					data-testid={item.testId}
					sx={{
						display: 'flex',
						flexDirection: 'column',
						borderRight:
							index < statisticsItems.length - 1
								? '1.5px solid #f0f0f0'
								: 'none',
						pr: index < statisticsItems.length - 1 ? 2 : 0,
						py: 2,
					}}
				>
					<Label label={item.label} />
					<Count value={item.value} />
					<StatusInfo statusInfo={statusInfo} />
				</Box>
			))}
		</Paper>
	);
}

JobStatisticsHeader.propTypes = {
	job: PropTypes.object.isRequired,
};

export default JobStatisticsHeader;
